import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getErrorCode, getErrorStatusCode } from "../../../../api/api-response";
import { ILoginAsResponse } from "../../../models/auth/login-as.response";
import { RestEndpoints } from "../../../../api/endpoints";
import getInstance, { instanceOfAxiosError } from "../../../../utils/rest";
import { SharedQueryKeys } from "../../query-keys";

const loginAs = async (email: string): Promise<ILoginAsResponse> => {
  const url = RestEndpoints.LoginAsUser.replace(":email", email);
  const { data } = await getInstance().post<ILoginAsResponse>(url);
  return data;
};

export const useLoginAsQuery = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email }: { email: string }) => loginAs(email),
    onError: (error, { email }) => {
      const backendMessage = instanceOfAxiosError(error)
        ? (error.response?.data as { message?: string })?.message
        : undefined;
      if (backendMessage) {
        enqueueSnackbar(t("basedata.users.login.errorLogin", { email, message: backendMessage }), { variant: "error" });
      } else {
        const errorMsg = getErrorCode(error);
        const errorCode = getErrorStatusCode(error);
        enqueueSnackbar(t("general.error_occurred", { errorCode, errorMsg }), { variant: "error" });
      }
    },
    onSuccess: () => {
      enqueueSnackbar(t("basedata.users.login.successLogin"), { variant: "success" });
      void queryClient.invalidateQueries({ queryKey: [SharedQueryKeys.GetUser] });
    },
  });
};
